import * as amplitude from "@amplitude/analytics-react-native";
import { Identify, Types } from "@amplitude/analytics-react-native";
import type { Destination } from "../types";

/**
 * The SDK's current user and device, to pass as event options with `track` and
 * `identify`. Otherwise the SDK's context plugin reads them from its config after
 * an `await` behind the timeline's `setTimeout(0)`, so a call followed by another
 * `identify` (logout, or the queue flushed at init) would be sent under the later
 * identity. Event options win over the plugin's values. A missing user id cannot
 * be passed (`user_id?: string`), so a call made while anonymous can still take a
 * user identified before the SDK processes it.
 */
function currentIdentity(): Types.EventOptions {
  const userId = amplitude.getUserId();
  const deviceId = amplitude.getDeviceId();
  return {
    ...(userId !== undefined && { user_id: userId }),
    ...(deviceId !== undefined && { device_id: deviceId }),
  };
}

/**
 * Sends events to Amplitude through the SDK's default instance. Experiment's
 * `initializeWithAmplitudeAnalytics` binds to that instance, so it must stay
 * the default one (not `createInstance()`), and the app must resolve the same
 * copy of `@amplitude/analytics-react-native` as this package (see
 * `apps/a3s-app/metro.config.js`).
 *
 * `appVersion` is sent as each event's app version. On iOS and Android the SDK
 * otherwise reads it from the native app; on web it has no source and sends none.
 */
export function amplitudeDestination(options: { apiKey: string; appVersion?: string | undefined }): Destination {
  return {
    // Not bounded here: `initAnalytics` stops waiting after 10s, but a late
    // success must still start sending, and a second `init` on the default
    // instance (which Experiment is bound to) is to be avoided.
    init: () =>
      amplitude.init(options.apiKey, undefined, {
        disableCookies: true,
        serverZone: "EU", // GDPR compliance — data must stay in EU. Do not change without legal review.
        logLevel: Types.LogLevel.Error,
        trackingSessionEvents: true,
        ...(options.appVersion !== undefined && { appVersion: options.appVersion }),
      }).promise,
    track: ({ name, properties, time }) => {
      // `time` is when `track` was called, so events queued before init keep their timestamps.
      amplitude.track(name, properties, { ...currentIdentity(), time });
    },
    identify: (user) => {
      if (user === null) {
        amplitude.reset();
        return;
      }
      amplitude.setUserId(user.userId);
      const entries = Object.entries(user.properties);
      if (entries.length === 0) {
        return;
      }
      const identify = new Identify();
      for (const [key, value] of entries) {
        if (value === null) {
          identify.unset(key);
        } else {
          // The SDK types arrays as mutable.
          identify.set(key, typeof value === "object" ? [...value] : value);
        }
      }
      // Sent under the user just set, even if a reset follows before the SDK processes it.
      amplitude.identify(identify, currentIdentity());
    },
  };
}
